// Admin email lists page. Two managed lists drive the contact-gate's blacklist
// evaluation (server/services/email-blacklist.js):
//   - Blacklist: addresses/domains/wildcards that are blocked outright.
//   - Allowlist: domains/addresses exempted from the disposable + MX layers
//     (it never overrides an explicit blacklist match).
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box, Typography, Alert, CircularProgress, Stack,
} from '@mui/material';
import { fetchEmailLists } from '../../store/admin-blacklist-slice.js';
import EmailListSection from '../../components/admin/blacklist/EmailListSection.jsx';
import SeededDomainsSection from '../../components/admin/blacklist/SeededDomainsSection.jsx';
import BuiltInDefaultsNote from '../../components/admin/blacklist/BuiltInDefaultsNote.jsx';
import AccessPolicySection from '../../components/admin/blacklist/AccessPolicySection.jsx';
import BlockedPageSection from '../../components/admin/blacklist/BlockedPageSection.jsx';

const AdminBlacklistPage = () => {
  const dispatch = useDispatch();
  const { data, loading, error } = useSelector((s) => s.adminBlacklist);

  useEffect(() => {
    dispatch(fetchEmailLists());
  }, [dispatch]);

  if (loading && data.blacklist.length === 0 && data.allowlist.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress aria-label="Loading email lists" />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 700 }}>
          Email Access
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Control which email addresses can reach the pricing form. Pick an access
          policy, then fine-tune it with your own allowed and blocked entries.
          Disposable-mail domains are always blocked automatically.
        </Typography>
        <BuiltInDefaultsNote />
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Stack spacing={3}>
        <AccessPolicySection />
        <EmailListSection
          list="blacklist"
          title="Blocked (manually added)"
          description="Addresses, domains, and wildcard patterns you've added by hand — e.g. specific competitor domains. The maintained disposable-mail list below is managed separately."
          entries={data.blacklist}
          typeOptions={['domain', 'exact', 'wildcard']}
        />
        <SeededDomainsSection />
        <EmailListSection
          list="allowlist"
          title="Allowed"
          description="Domains or addresses exempted from the disposable-mail and MX checks."
          entries={data.allowlist}
          typeOptions={['domain', 'exact']}
        />
        <BlockedPageSection />
      </Stack>
    </Box>
  );
};

export default AdminBlacklistPage;
